/**
 * Admin UI kit — the building blocks every admin screen is made of, so forms,
 * uploads and lists look and behave the same everywhere.
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { api } from './api';

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

export const PageHeader = ({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) => (
  <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
    <div className="min-w-0">
      <h1 className="text-2xl font-bold text-brand-ink">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-brand-muted max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Page = ({ children, wide = false }: { children: ReactNode; wide?: boolean }) => (
  <div className={`p-4 sm:p-6 lg:p-8 ${wide ? '' : 'max-w-5xl'}`}>{children}</div>
);

export const Panel = ({ title, hint, children, actions }: { title?: string; hint?: ReactNode; children: ReactNode; actions?: ReactNode }) => (
  <section className="bg-white rounded-2xl border border-brand-line shadow-soft p-5 sm:p-6 mb-5">
    {(title || actions) && (
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          {title && <h2 className="text-base font-semibold text-brand-ink">{title}</h2>}
          {hint && <p className="mt-0.5 text-xs text-brand-muted">{hint}</p>}
        </div>
        {actions}
      </div>
    )}
    {children}
  </section>
);

export const Loading = ({ label = 'Loading…' }: { label?: string }) => (
  <div className="flex items-center gap-3 text-sm text-brand-muted py-10 justify-center">
    <span className="w-5 h-5 rounded-full border-2 border-brand-line border-t-brand-blue animate-spin" />
    {label}
  </div>
);

export const ErrorBox = ({ error, onRetry }: { error: string; onRetry?: () => void }) => (
  <div className="rounded-xl border border-brand-red/30 bg-brand-red/5 p-4 text-sm text-brand-red flex flex-wrap items-center justify-between gap-3">
    <span>{error}</span>
    {onRetry && (
      <button type="button" className="vp-btn vp-btn-outline vp-btn-sm" onClick={onRetry}>
        Try again
      </button>
    )}
  </div>
);

export const Empty = ({ children }: { children: ReactNode }) => (
  <div className="rounded-2xl border-2 border-dashed border-brand-line p-10 text-center text-sm text-brand-muted">{children}</div>
);

export const Badge = ({ tone = 'grey', children }: { tone?: 'grey' | 'green' | 'blue' | 'gold' | 'red'; children: ReactNode }) => {
  const tones = {
    grey: 'bg-brand-surface text-brand-muted',
    green: 'bg-success-soft text-success',
    blue: 'bg-brand-blue/10 text-brand-blue',
    gold: 'bg-brand-gold/30 text-brand-ink',
    red: 'bg-brand-red/10 text-brand-red',
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>{children}</span>;
};

/* ------------------------------------------------------------------ */
/* Form fields                                                          */
/* ------------------------------------------------------------------ */

export const Field = ({ label, hint, children, required }: { label: string; hint?: ReactNode; children: ReactNode; required?: boolean }) => (
  <label className="block">
    <span className="block text-sm font-medium text-brand-ink mb-1.5">
      {label}
      {required && <span className="text-brand-red"> *</span>}
    </span>
    {children}
    {hint && <span className="block mt-1 text-xs text-brand-muted">{hint}</span>}
  </label>
);

const inputCls = 'vp-input w-full';

export const TextInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => <input {...props} className={`${inputCls} ${props.className || ''}`} />;

export const TextArea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea rows={4} {...props} className={`${inputCls} min-h-[6rem] ${props.className || ''}`} />
);

export const Select = ({ options, ...props }: React.SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) => (
  <select {...props} className={`${inputCls} ${props.className || ''}`}>
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

export const Toggle = ({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) => (
  <label className="flex items-start gap-3 cursor-pointer select-none">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative mt-0.5 w-10 h-6 rounded-full flex-shrink-0 transition-colors ${checked ? 'bg-brand-blue' : 'bg-brand-line'}`}
    >
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${checked ? 'left-[1.125rem]' : 'left-0.5'}`} />
    </button>
    <span>
      <span className="block text-sm font-medium text-brand-ink">{label}</span>
      {hint && <span className="block text-xs text-brand-muted">{hint}</span>}
    </span>
  </label>
);

/** Small icon-ish buttons for list rows. */
export const MiniButton = ({ children, danger, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }) => (
  <button
    type="button"
    {...props}
    className={`inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-lg border text-xs font-medium transition-colors disabled:opacity-40 ${
      danger ? 'border-brand-red/30 text-brand-red hover:bg-brand-red/5' : 'border-brand-line text-brand-ink hover:bg-brand-surface'
    } ${props.className || ''}`}
  >
    {children}
  </button>
);

/** Move an item in an array (used for ↑ / ↓ reordering). */
export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** A list of short strings (features, "best for", bullet points). */
export const StringList = ({ value, onChange, placeholder, max = 20 }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; max?: number }) => (
  <div className="space-y-2">
    {value.map((v, i) => (
      <div key={i} className="flex gap-2">
        <TextInput value={v} placeholder={placeholder} onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} />
        <MiniButton onClick={() => onChange(move(value, i, i - 1))} disabled={i === 0} aria-label="Move up">
          ↑
        </MiniButton>
        <MiniButton onClick={() => onChange(move(value, i, i + 1))} disabled={i === value.length - 1} aria-label="Move down">
          ↓
        </MiniButton>
        <MiniButton danger onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remove">
          ✕
        </MiniButton>
      </div>
    ))}
    {value.length < max && (
      <button type="button" className="vp-btn vp-btn-outline vp-btn-sm" onClick={() => onChange([...value, ''])}>
        + Add
      </button>
    )}
  </div>
);

/** Title + text pairs (product benefits, "look and feel"). */
export const PairList = ({
  value,
  onChange,
  titleLabel = 'Title',
  textLabel = 'Description',
  max = 20,
}: {
  value: { title: string; text: string }[];
  onChange: (v: { title: string; text: string }[]) => void;
  titleLabel?: string;
  textLabel?: string;
  max?: number;
}) => (
  <div className="space-y-3">
    {value.map((v, i) => (
      <div key={i} className="rounded-xl border border-brand-line p-3 bg-brand-surface/50">
        <div className="flex gap-2">
          <TextInput value={v.title} placeholder={titleLabel} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
          <MiniButton onClick={() => onChange(move(value, i, i - 1))} disabled={i === 0} aria-label="Move up">
            ↑
          </MiniButton>
          <MiniButton onClick={() => onChange(move(value, i, i + 1))} disabled={i === value.length - 1} aria-label="Move down">
            ↓
          </MiniButton>
          <MiniButton danger onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Remove">
            ✕
          </MiniButton>
        </div>
        <TextArea rows={2} className="mt-2" value={v.text} placeholder={textLabel} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
      </div>
    ))}
    {value.length < max && (
      <button type="button" className="vp-btn vp-btn-outline vp-btn-sm" onClick={() => onChange([...value, { title: '', text: '' }])}>
        + Add
      </button>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/* Uploads                                                              */
/* ------------------------------------------------------------------ */

const ACCEPT = { image: 'image/jpeg,image/png,image/webp,image/avif,image/gif', pdf: 'application/pdf' };

/** Upload helper with busy/error state, shared by the file fields. */
function useUpload(prefix: string) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = useCallback(
    async (files: File[]): Promise<string[]> => {
      setBusy(true);
      setError('');
      try {
        const out: string[] = [];
        for (const f of files) out.push(await api.uploadFile(f, prefix));
        return out;
      } catch (e: any) {
        setError(e.message || 'Upload failed');
        return [];
      } finally {
        setBusy(false);
      }
    },
    [prefix]
  );
  return { busy, error, run };
}

/** One image or PDF: shows a preview, lets you replace or remove it. */
export const FileField = ({
  value,
  onChange,
  kind = 'image',
  prefix = 'uploads',
  aspect = 'aspect-[4/3]',
  checker = false,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
  kind?: 'image' | 'pdf';
  prefix?: string;
  aspect?: string;
  /** Show a checkerboard behind the preview (for transparent cut-outs). */
  checker?: boolean;
}) => {
  const input = useRef<HTMLInputElement>(null);
  const { busy, error, run } = useUpload(prefix);
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const [url] = await run(files.slice(0, 1));
    if (url) onChange(url);
  };
  return (
    <div>
      <input ref={input} type="file" accept={ACCEPT[kind]} className="hidden" onChange={pick} />
      {value ? (
        kind === 'image' ? (
          <div className={`relative ${aspect} w-full max-w-xs rounded-xl overflow-hidden border border-brand-line ${checker ? 'vp-checker' : 'bg-brand-surface'}`}>
            <img src={value} alt="" className="absolute inset-0 w-full h-full object-contain" />
          </div>
        ) : (
          <a href={value} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-brand-line bg-brand-surface px-3 py-2 text-sm text-brand-blue hover:underline max-w-full">
            <span className="font-bold text-brand-red">PDF</span>
            <span className="truncate">{decodeURIComponent(value.split('/').pop() || 'document.pdf')}</span>
          </a>
        )
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className={`${kind === 'image' ? `${aspect} w-full max-w-xs` : 'w-full max-w-xs py-5'} rounded-xl border-2 border-dashed border-brand-line text-sm text-brand-muted hover:border-brand-blue hover:text-brand-blue transition-colors flex items-center justify-center`}
        >
          {busy ? 'Uploading…' : kind === 'image' ? '+ Upload photo' : '+ Upload PDF'}
        </button>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {value && (
          <>
            <MiniButton onClick={() => input.current?.click()} disabled={busy}>
              {busy ? 'Uploading…' : 'Replace'}
            </MiniButton>
            <MiniButton danger onClick={() => onChange(null)}>
              Remove
            </MiniButton>
          </>
        )}
      </div>
      {error && <p className="mt-1 text-xs text-brand-red">{error}</p>}
    </div>
  );
};

/** Several photos, each with alt text, reorderable. */
export const GalleryField = ({ value, onChange, prefix = 'uploads', max = 24 }: { value: { src: string; alt: string }[]; onChange: (v: { src: string; alt: string }[]) => void; prefix?: string; max?: number }) => {
  const input = useRef<HTMLInputElement>(null);
  const { busy, error, run } = useUpload(prefix);
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, Math.max(0, max - value.length));
    e.target.value = '';
    if (!files.length) return;
    const urls = await run(files);
    onChange([...value, ...urls.map((src) => ({ src, alt: '' }))]);
  };
  return (
    <div>
      <input ref={input} type="file" accept={ACCEPT.image} multiple className="hidden" onChange={pick} />
      {value.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          {value.map((g, i) => (
            <div key={g.src + i} className="rounded-xl border border-brand-line overflow-hidden bg-white">
              <div className="relative aspect-[4/3] bg-brand-surface">
                <img src={g.src} alt="" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="p-2 space-y-2">
                <TextInput value={g.alt} placeholder="Describe the photo (for accessibility)" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)))} />
                <div className="flex gap-1.5">
                  <MiniButton onClick={() => onChange(move(value, i, i - 1))} disabled={i === 0} aria-label="Move earlier">
                    ←
                  </MiniButton>
                  <MiniButton onClick={() => onChange(move(value, i, i + 1))} disabled={i === value.length - 1} aria-label="Move later">
                    →
                  </MiniButton>
                  <MiniButton danger className="ml-auto" onClick={() => onChange(value.filter((_, j) => j !== i))}>
                    Remove
                  </MiniButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {value.length < max && (
        <button type="button" className="vp-btn vp-btn-outline vp-btn-sm" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Uploading…' : '+ Add photos'}
        </button>
      )}
      {error && <p className="mt-1 text-xs text-brand-red">{error}</p>}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Toasts                                                               */
/* ------------------------------------------------------------------ */

type Toast = { id: number; text: string; tone: 'ok' | 'error' };
const ToastCtx = createContext<(text: string, tone?: Toast['tone']) => void>(() => undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast['tone'] = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 6000 : 3000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] space-y-2 max-w-sm" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`rounded-xl px-4 py-3 text-sm font-medium shadow-lift ${t.tone === 'ok' ? 'bg-brand-navy text-white' : 'bg-brand-red text-white'}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
};

export const useToast = () => useContext(ToastCtx);

/** Browser confirm wrapped so every delete asks the same way. */
export const confirmDelete = (what: string) => window.confirm(`Delete ${what}? This cannot be undone.`);

export const fmtDate = (iso: string | null | undefined, withTime = false) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', withTime ? { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' } : { day: 'numeric', month: 'short', year: 'numeric' });
};
